import { Button, Dialog, DialogActions, DialogContent, Grid, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { createRef, useState } from 'react';
import { LongLoading } from '../../../components/Elements/Loading/LongLoading';

interface DialogViewerProps {
  type: 'image' | 'video' | 'pdf';
  url: string;
  isOpenModal: boolean;
  onClose: () => void;
  contentType?: string;
  onClickButton: () => Promise<void>;
  isAlreadyUnmasked: boolean;
}

const DialogViewer: React.FC<DialogViewerProps> = ({
  type,
  url,
  isOpenModal,
  onClose,
  contentType,
  onClickButton,
  isAlreadyUnmasked
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isUnmasked, setIsUnmasked] = useState(false);
  const downloadRef = createRef<HTMLAnchorElement>();

  let label = '';
  if (type === 'image') {
    label = '画像';
  } else if (type === 'video') {
    label = '動画';
  } else if (type === 'pdf') {
    label = 'PDF';
  }

  const handleClick = async () => {
    setIsLoading(true);

    if (window.injectedData?.OSType) {
      if ('ReactNativeWebView' in window) {
        window.ReactNativeWebView?.postMessage(JSON.stringify({ fileUrl: url }));
      }
      setIsLoading(false);
      return;
    }
    try {
      // マスク解除済みならダウンロード
      if (isAlreadyUnmasked || isUnmasked) {
        if (!downloadRef.current) return;

        const res = await fetch(url);
        const blob = await res.blob(); // blobで取得
        downloadRef.current.href = URL.createObjectURL(blob); // BlobをURLに変換
        downloadRef.current.click();
      } else {
        // マスク未解除の場合
        await onClickButton();
        setIsUnmasked(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // リセット
    setIsUnmasked(false);
    onClose();
  };

  return (
    <>
      <Dialog fullScreen open={isOpenModal} onClose={onClose} maxWidth="lg">
        <DialogActions
          sx={{
            position: 'absolute',
            zIndex: 1
          }}
        >
          <IconButton
            onClick={handleClose}
            aria-label="close"
            sx={{
              color: '#EBEBF599',
              borderRadius: '50%',
              width: 30,
              height: 30,
              boxShadow: 3,
              '&:hover': { backgroundColor: '#f0f0f0' }
            }}
          >
            <Close />
          </IconButton>
        </DialogActions>
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            backgroundColor: 'black',
            padding: 0,
            position: 'relative'
          }}
        >
          {type === 'image' && (
            <img src={url} alt="Attachment" style={{ maxWidth: '100vw', maxHeight: '100vh', objectFit: 'contain' }} />
          )}
          {type === 'video' && (
            <video controls key={url}>
              <source src={url} type={contentType} />
            </video>
          )}
          {type === 'pdf' && (
            <iframe id={url} title={url} src={`${url}#toolbar=0&navpanes=0`} className="w-full h-[60vh]" />
          )}
          <Grid
            width={353}
            container
            rowSpacing={2}
            direction={'column'}
            style={{ position: 'absolute', bottom: '40px' }}
          >
            <Grid item className="mx-4">
              {isAlreadyUnmasked || isUnmasked ? (
                <></>
              ) : (
                <Button fullWidth className="text-white bg-primary py-2 font-semibold w-full" onClick={handleClick}>
                  {label}を鮮明にする
                </Button>
              )}
            </Grid>
            <Grid item>
              <Typography
                align="center"
                className="text-sm"
                color={isAlreadyUnmasked || isUnmasked ? 'black' : 'white'}
              >
                取得には2~3分ほどかかります
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <a style={{ display: 'none' }} ref={downloadRef} />
      {isLoading && <LongLoading />}
    </>
  );
};

export default DialogViewer;
